import { usePrintStudentWork } from '../../../library/printing/StudentWorkPrinter';
import React, { useMemo, useState } from 'react';
import { ClassroomTeam, PortfolioEntry } from '../../classroom/types';
import { ResolvedSessionPlan, ResolvedSessionSlot } from '../../utils/sessionPlan';
import {
  classPhotoArchiveItems,
  downloadClassPhotos,
} from '../../classroom/classPhotoDownload';

interface ClassPhotoDownloadProps {
  squadName: string;
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
  plan: ResolvedSessionPlan;
  slot?: ResolvedSessionSlot;
}

const ClassPhotoDownload: React.FC<ClassPhotoDownloadProps> = ({
  squadName,
  teams,
  entries,
  plan,
  slot,
}) => {
  const printStudentWork = usePrintStudentWork();
  const defaultIndex = slot
    ? Math.max(0, plan.slots.findIndex((item) => item.index === slot.index))
    : Math.max(0, plan.slots.findIndex((item) => item.status === 'live' || item.status === 'next'));
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const [busy, setBusy] = useState(false);
  const selectedSlot = slot || plan.slots[selectedIndex];
  const downloadCount = useMemo(
    () => selectedSlot ? classPhotoArchiveItems({ squadName, slot: selectedSlot, teams, entries }).length : 0,
    [entries, selectedSlot, squadName, teams]
  );

  const handleDownload = async () => {
    if (!selectedSlot || !downloadCount) return;
    setBusy(true);
    try {
      await downloadClassPhotos({ squadName, slot: selectedSlot, teams, entries });
    } catch (error) {
      console.error(error);
      void window.appAlert('Could not download all class photos and AI artifacts. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cd-class-photo-download">
      {!slot && (
        <label>
          <span className="cd-sr-only">Class to download</span>
          <select
            value={selectedIndex}
            onChange={(event) => setSelectedIndex(Number(event.target.value))}
            aria-label="Class to download"
          >
            {plan.slots.map((item, index) => (
              <option key={`${item.index}-${item.levelId}-${item.date || ''}`} value={index}>
                Class {item.index} · {item.levelTitle}{item.date ? ` · ${item.date}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      <button
        type="button"
        className="cd-btn"
        disabled={busy || !downloadCount}
        onClick={() => void handleDownload()}
        title={downloadCount ? `Download ${downloadCount} class portfolio item${downloadCount === 1 ? '' : 's'} by team` : 'No photos or AI artifacts for this class yet'}
      >
        {busy ? 'Preparing portfolio…' : `↓ Download photos & AI${downloadCount ? ` (${downloadCount})` : ''}`}
      </button>
      <button type="button" className="cd-btn" onClick={() => printStudentWork(selectedSlot?.index)}>Print student work · Universe cards PDF</button>
    </div>
  );
};

export default ClassPhotoDownload;
